import React, { useState } from 'react';
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Typography,
  Box,
  CircularProgress,
  Divider,
  Alert,
  Grid,
  Paper
} from '@mui/material';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import api from '../api/axios';

// Define available fields for export
const AVAILABLE_FIELDS = [
  // Basic information
  { id: 'first_name', label: 'First Name', category: 'basic', default: true },
  { id: 'last_name', label: 'Last Name', category: 'basic', default: true },
  { id: 'email', label: 'Email', category: 'basic', default: true },
  { id: 'date_of_birth', label: 'Date of Birth', category: 'basic', default: true },
  { id: 'preferred_first_name', label: 'Preferred Name', category: 'basic', default: false },
  { id: 'gender_identity', label: 'Gender Identity', category: 'basic', default: false },
  
  // Contact details
  { id: 'phone', label: 'Phone Numbers', category: 'contact', default: true },
  { id: 'address_1', label: 'Address Line 1', category: 'contact', default: false },
  { id: 'address_2', label: 'Address Line 2', category: 'contact', default: false },
  { id: 'city', label: 'City', category: 'contact', default: false },
  { id: 'state', label: 'State', category: 'contact', default: false },
  { id: 'post_code', label: 'Postal Code', category: 'contact', default: false },
  { id: 'country', label: 'Country', category: 'contact', default: false },
  
  // Medical information
  { id: 'notes', label: 'Notes', category: 'medical', default: false },
  { id: 'occupation', label: 'Occupation', category: 'medical', default: false },
  { id: 'appointment_notes', label: 'Appointment Notes', category: 'medical', default: false },
  { id: 'referral_source', label: 'Referral Source', category: 'medical', default: false },
  
  // Preferences
  { id: 'receives_confirmation_emails', label: 'Receives Confirmations', category: 'preferences', default: false },
  { id: 'receives_cancellation_emails', label: 'Receives Cancellations', category: 'preferences', default: false },
  { id: 'accepted_email_marketing', label: 'Accepts Email Marketing', category: 'preferences', default: false }
];

// Group fields by category for better organization
const FIELD_CATEGORIES = [
  { id: 'basic', label: 'Basic Information' },
  { id: 'contact', label: 'Contact Details' },
  { id: 'medical', label: 'Medical Information' },
  { id: 'preferences', label: 'Communication Preferences' }
];

interface ExportPatientsProps {
  onClose?: () => void;
}

const ExportPatients: React.FC<ExportPatientsProps> = ({ onClose }) => {
  // State for tracking selected fields
  const [selectedFields, setSelectedFields] = useState<string[]>(
    AVAILABLE_FIELDS.filter(field => field.default).map(field => field.id)
  );
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Open the export dialog
  const handleOpen = () => {
    setOpen(true);
  };

  // Close the dialog
  const handleClose = () => {
    setOpen(false);
    if (onClose) onClose();
  };

  // Toggle a field selection
  const handleFieldToggle = (fieldId: string) => {
    setSelectedFields(prev => {
      if (prev.includes(fieldId)) {
        return prev.filter(id => id !== fieldId);
      } else {
        return [...prev, fieldId];
      }
    });
  };

  // Toggle all fields in a category
  const handleCategoryToggle = (categoryId: string) => {
    const categoryFields = AVAILABLE_FIELDS
      .filter(field => field.category === categoryId)
      .map(field => field.id);

    // Check if all fields in the category are already selected
    const allSelected = categoryFields.every(fieldId => selectedFields.includes(fieldId));

    if (allSelected) {
      // If all are selected, deselect them
      setSelectedFields(prev => prev.filter(id => !categoryFields.includes(id)));
    } else {
      // If not all are selected, select all in the category
      const newSelectedFields = [...selectedFields];
      categoryFields.forEach(fieldId => {
        if (!newSelectedFields.includes(fieldId)) {
          newSelectedFields.push(fieldId);
        }
      });
      setSelectedFields(newSelectedFields);
    }
  };

  // Generate and download the CSV
  const handleExport = async () => {
    if (selectedFields.length === 0) {
      setError('Please select at least one field to export');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Request CSV data from backend
      const response = await api.post('/api/patients/export', {
        fields: selectedFields
      }, {
        responseType: 'blob' // Important for file downloads
      });

      // Create a download link for the CSV blob
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      
      // Generate filename with current date
      const date = new Date().toISOString().split('T')[0];
      link.setAttribute('download', `patient_export_${date}.csv`);
      
      // Trigger download
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Close dialog after successful export
      handleClose();
    } catch (err) {
      console.error('Error exporting patient data:', err);
      setError('Failed to export patient data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Check if all fields in a category are selected
  const isCategorySelected = (categoryId: string): boolean => {
    const categoryFields = AVAILABLE_FIELDS
      .filter(field => field.category === categoryId)
      .map(field => field.id);
    
    return categoryFields.every(fieldId => selectedFields.includes(fieldId));
  };

  // Check if some (but not all) fields in a category are selected
  const isCategoryIndeterminate = (categoryId: string): boolean => {
    const categoryFields = AVAILABLE_FIELDS
      .filter(field => field.category === categoryId)
      .map(field => field.id);
    
    const selectedCount = categoryFields.filter(fieldId => selectedFields.includes(fieldId)).length;
    return selectedCount > 0 && selectedCount < categoryFields.length;
  };

  return (
    <>
      <Button
        variant="outlined"
        color="primary"
        startIcon={<FileDownloadIcon />}
        onClick={handleOpen}
      >
        Export Patients
      </Button>

      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle>
          Export Patient Data
        </DialogTitle>
        
        <DialogContent>
          <Typography variant="body1" paragraph sx={{ mt: 1 }}>
            Export your patient data as a CSV file for use in email campaigns or other systems.
            Select the fields you want to include in the export.
          </Typography>
          
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Paper sx={{ p: 2, mb: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Export Details
            </Typography>
            <Typography variant="body2" color="text.secondary">
              • Only patients in your local database will be exported<br />
              • Exported file will be in CSV format<br />
              • Data will be downloaded directly to your computer<br />
              • Choose which fields to include below
            </Typography>
          </Paper>
          
          <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
            Select Fields to Include
          </Typography>

          <Box sx={{ mt: 2 }}>
            {FIELD_CATEGORIES.map(category => (
              <Box key={category.id} sx={{ mb: 3 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={isCategorySelected(category.id)}
                      indeterminate={isCategoryIndeterminate(category.id)}
                      onChange={() => handleCategoryToggle(category.id)}
                    />
                  }
                  label={<Typography variant="subtitle1">{category.label}</Typography>}
                />
                
                <Divider sx={{ my: 1 }} />
                
                <Grid container spacing={2}>
                  {AVAILABLE_FIELDS
                    .filter(field => field.category === category.id)
                    .map(field => (
                      <Grid item xs={12} sm={6} md={4} key={field.id}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={selectedFields.includes(field.id)}
                              onChange={() => handleFieldToggle(field.id)}
                            />
                          }
                          label={field.label}
                        />
                      </Grid>
                    ))}
                </Grid>
              </Box>
            ))}
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={handleClose} color="inherit">
            Cancel
          </Button>
          <Button
            onClick={handleExport}
            variant="contained"
            color="primary"
            disabled={loading || selectedFields.length === 0}
            startIcon={loading ? <CircularProgress size={20} /> : <FileDownloadIcon />}
          >
            {loading ? 'Exporting...' : 'Export Selected Fields'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ExportPatients;